import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { router, type Href } from 'expo-router';
import React, { useMemo, useRef, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useMembership } from '@/hooks/use-membership';
import {
  providerKeys,
  usePlanUsage,
  useProviderProfile,
  useSubscription,
} from '@/hooks/use-provider';
import { useOrganization } from '@/hooks/use-team';
import { useTerms } from '@/hooks/use-terms';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';
import api from '@/services/api';
import { openManageAppleSubscriptions } from '@/services/appleIapService';
import { openManageGoogleSubscriptions } from '@/services/googlePlayIapService';
import { useToastStore } from '@/store/toastStore';
import { isInTrialPeriod, trialDaysRemaining } from '@/utils/trial';

/**
 * Configurações > Seu plano: o bloco escuro do topo (mesmo hero de
 * Relatórios). Plano atual, estado, teste grátis e o medidor de clientes do
 * mês. Quem decide se aparece é a tela (só dono, fora do foco).
 */
export function PlanHero() {
  const { addToast } = useToastStore();
  const { membership } = useMembership();
  const terms = useTerms();

  const { data: providerData, refetch: refetchProvider } = useProviderProfile();
  const { data: subscriptionData, refetch: refetchSubscription } = useSubscription();
  const queryClient = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const { data: planUsage, refetch: refetchPlanUsage } = usePlanUsage();
  const { data: plansForDisplay } = useQuery({
    queryKey: ['stripe-plans'],
    queryFn: async () => {
      const res = await api.get('/stripe/plans');
      return res.data as { id: string; name: string; capacity?: string }[];
    },
    staleTime: 5 * 60 * 1000,
  });
  const { data: organization } = useOrganization({ enabled: membership?.type === 'TEAM' });

  const isFirstFocus = useRef(true);
  useFocusEffect(
    React.useCallback(() => {
      if (isFirstFocus.current) {
        isFirstFocus.current = false;
        return;
      }
      refetchProvider();
      refetchPlanUsage();
    }, [refetchProvider, refetchPlanUsage]),
  );

  // Fonte da verdade da assinatura é o próprio /provider/me: `subscriptionStatus`
  // é gravado tanto pelo fluxo Stripe quanto pelo Apple IAP. O /stripe/subscription
  // só entra como enriquecimento — nome do produto e data da próxima cobrança.
  const subscriptionStatus = providerData?.subscriptionStatus ?? subscriptionData?.status ?? null;
  const subscriptionActive =
    subscriptionStatus === 'active' ||
    subscriptionStatus === 'trialing' ||
    subscriptionStatus === 'past_due';
  const isAppleIapSubscription = providerData?.subscriptionPaymentMethod === 'apple_iap';
  const isGooglePlaySubscription = providerData?.subscriptionPaymentMethod === 'google_play';
  const isStoreSubscription = isAppleIapSubscription || isGooglePlaySubscription;
  const inTrial = !subscriptionActive && isInTrialPeriod(providerData?.trialEndsAt ?? null);

  const selectedPlanInfo = useMemo(() => {
    const planId = providerData?.subscriptionPlanId;
    if (!planId) return null;
    return plansForDisplay?.find((p) => p.id === planId) ?? null;
  }, [providerData?.subscriptionPlanId, plansForDisplay]);

  // Plano de equipe: o que vale é o contratado pela organização, não a faixa solo.
  const teamContract =
    organization?.contractedProfessionals && organization.contractedClients
      ? {
          professionals: organization.contractedProfessionals,
          clients: organization.contractedClients,
        }
      : null;

  const planLabel = teamContract
    ? 'Equipe'
    : (selectedPlanInfo?.name ??
      subscriptionData?.planName ??
      (subscriptionActive ? 'Plano ativo' : 'Nenhum plano'));
  const planCapacity = teamContract
    ? `${teamContract.professionals} ${terms.professionalPlural} · até ${teamContract.clients} ${terms.clientPlural}/mês`
    : (selectedPlanInfo?.capacity ?? subscriptionData?.capacity ?? null);

  // Próxima renovação: o /provider/me guarda em `subscriptionPeriodEnd` (ISO);
  // o Stripe manda `currentPeriodEnd` em epoch (segundos).
  const renewsAt = providerData?.subscriptionPeriodEnd
    ? new Date(providerData.subscriptionPeriodEnd)
    : subscriptionData?.currentPeriodEnd
      ? new Date(subscriptionData.currentPeriodEnd * 1000)
      : null;

  const statusLabel = subscriptionActive
    ? subscriptionStatus === 'past_due'
      ? 'Pagamento pendente'
      : 'Ativo'
    : inTrial
      ? 'Teste grátis'
      : 'Inativo';

  const detail = subscriptionActive
    ? subscriptionData?.cancelAtPeriodEnd && renewsAt
      ? `Cancelada. Você usa o plano até ${renewsAt.toLocaleDateString('pt-BR')}.`
      : renewsAt
      ? `${isStoreSubscription ? 'Renova em' : 'Próxima cobrança em'} ${renewsAt.toLocaleDateString('pt-BR')}`
      : null
    : inTrial
      ? `Restam ${trialDaysRemaining(providerData?.trialEndsAt ?? null)} dia(s) de teste grátis.`
      : 'Sem assinatura ativa. Escolha um plano para manter seus limites de clientes.';

  // Cancelar pelo app só vale para quem assinou pela web: Apple e Google
  // cancelam (e reembolsam) na própria loja.
  const canCancelHere = subscriptionActive && !isStoreSubscription && !!subscriptionData;
  const cancelScheduled = !!subscriptionData?.cancelAtPeriodEnd;
  const refundableUntil = subscriptionData?.refundableUntil
    ? new Date(subscriptionData.refundableUntil * 1000)
    : null;

  const cancelMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ canceledAtPeriodEnd: boolean; refundedCents: number }>(
          '/stripe/subscription/cancel',
        )
      ).data,
    onSuccess: (result) => {
      addToast(
        result.canceledAtPeriodEnd
          ? 'Assinatura cancelada. Você usa o plano até o fim do período pago.'
          : result.refundedCents > 0
            ? `Assinatura cancelada. Devolvemos ${formatCents(result.refundedCents)} no seu cartão.`
            : 'Assinatura cancelada.',
        'success',
      );
      void queryClient.invalidateQueries({ queryKey: providerKeys.all });
    },
    onError: (err: any) =>
      addToast(err?.response?.data?.message ?? 'Não foi possível cancelar agora.', 'error'),
  });

  const resumeMutation = useMutation({
    mutationFn: async () => (await api.post('/stripe/subscription/resume')).data,
    onSuccess: () => {
      addToast('Cancelamento desfeito. Sua assinatura continua.', 'success');
      void refetchSubscription();
    },
    onError: () => addToast('Não foi possível desfazer o cancelamento agora.', 'error'),
  });

  const used = planUsage?.used ?? 0;
  const limit = planUsage?.limit ?? null;
  const usagePct = limit && limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const usageNote = planUsage?.newClientsLocked
    ? 'Clientes novos só entram depois do upgrade. Quem já é cliente continua marcando.'
    : limit && used >= limit
      ? 'Limite atingido. Este mês ainda aceita clientes novos.'
      : null;

  return (
    <View
      className="w-full gap-4 rounded-3xl bg-ink p-5"
      style={{ borderCurve: 'continuous', boxShadow: '0 12px 32px rgba(10, 10, 10, 0.25)' }}
    >
      <View className="flex-row items-center justify-between gap-3">
        <ThemedText className="font-medium text-sm text-white/60">Seu plano</ThemedText>
        <View className="rounded-full bg-white/10 px-2.5 py-1">
          <ThemedText
            className={`font-semibold text-xs ${
              subscriptionStatus === 'past_due'
                ? 'text-[#F87171]'
                : subscriptionActive || inTrial
                  ? 'text-[#4ADE80]'
                  : 'text-white/70'
            }`}
          >
            {statusLabel}
          </ThemedText>
        </View>
      </View>

      <View className="gap-1">
        <ThemedText className="font-bold text-3xl text-white" numberOfLines={1}>
          {planLabel}
        </ThemedText>
        {planCapacity ? (
          <ThemedText className="text-sm text-white/60">{planCapacity}</ThemedText>
        ) : null}
        {detail ? <ThemedText className="text-sm text-white/60">{detail}</ThemedText> : null}
      </View>

      {limit != null && limit > 0 && (
        <View className="gap-2 rounded-2xl bg-white/10 p-3">
          <View className="flex-row items-center justify-between">
            <ThemedText className="text-xs text-white/60">Clientes atendidos este mês</ThemedText>
            <ThemedText className="font-semibold text-sm text-white">
              {used}/{limit}
            </ThemedText>
          </View>
          <View className="h-1.5 overflow-hidden rounded-full bg-white/10">
            <View className="h-full rounded-full bg-primary" style={{ width: `${usagePct}%` }} />
          </View>
          {usageNote ? (
            <ThemedText className="text-xs text-white/60">{usageNote}</ThemedText>
          ) : null}
        </View>
      )}

      <Button
        variant="outline"
        // Branco fixo nos dois temas: o hero continua escuro no tema escuro.
        style={{ backgroundColor: '#FFFFFF', borderColor: '#FFFFFF' }}
        onPress={() =>
          void navigateToSubscriptionPlans({ push: (path) => router.push(path as Href) })
        }
      >
        <ThemedText className="font-semibold text-sm" style={{ color: '#171717' }}>
          {subscriptionActive ? 'Alterar plano' : 'Ver planos'}
        </ThemedText>
      </Button>

      {isAppleIapSubscription && (
        <Pressable
          onPress={async () => {
            try {
              await openManageAppleSubscriptions();
            } catch {
              addToast('Não foi possível abrir o gerenciamento de assinatura.', 'error');
            }
          }}
          className="flex-row items-center justify-center gap-2"
        >
          <Ionicons name="open-outline" size={15} color="rgba(255,255,255,0.7)" />
          <ThemedText className="text-sm text-white/70">
            Gerenciar assinatura na App Store
          </ThemedText>
        </Pressable>
      )}

      {canCancelHere && cancelScheduled && (
        <Pressable
          disabled={resumeMutation.isPending}
          onPress={() => resumeMutation.mutate()}
          className="items-center"
        >
          <ThemedText className="text-sm text-white/70">Desfazer cancelamento</ThemedText>
        </Pressable>
      )}

      {canCancelHere && !cancelScheduled && (
        <Pressable
          disabled={cancelMutation.isPending}
          onPress={() => setConfirmCancel(true)}
          className="items-center"
        >
          <ThemedText className="text-sm text-white/70">Cancelar assinatura</ThemedText>
        </Pressable>
      )}

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Cancelar assinatura?"
        message={
          refundableUntil
            ? `Você está dentro dos 7 dias da contratação: o plano é encerrado agora e devolvemos todo o valor pago no seu cartão. O estorno aparece na fatura em até 10 dias úteis.`
            : `O plano continua até ${renewsAt ? renewsAt.toLocaleDateString('pt-BR') : 'o fim do período pago'} e não haverá nova cobrança. Passados 7 dias da contratação, o valor já pago não é devolvido.`
        }
        confirmLabel="Cancelar assinatura"
        cancelLabel="Manter plano"
        destructive
        onConfirm={() => cancelMutation.mutate()}
      />

      {isGooglePlaySubscription && Platform.OS === 'android' && (
        <Pressable
          onPress={async () => {
            try {
              await openManageGoogleSubscriptions(providerData?.subscriptionPlanId);
            } catch {
              addToast('Não foi possível abrir o gerenciamento de assinatura.', 'error');
            }
          }}
          className="flex-row items-center justify-center gap-2"
        >
          <Ionicons name="open-outline" size={15} color="rgba(255,255,255,0.7)" />
          <ThemedText className="text-sm text-white/70">
            Gerenciar assinatura no Google Play
          </ThemedText>
        </Pressable>
      )}
    </View>
  );
}

function formatCents(cents: number) {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
